import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import CreditDetailsModal from './CreditDetailsModal';
import ProvenanceModal from './ProvenanceModal';

const CreditTable = ({ credits, selectedCredits, onSelectionChange, onBulkAction }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [detailsModal, setDetailsModal] = useState({ isOpen: false, credit: null });
  const [provenanceModal, setProvenanceModal] = useState({ isOpen: false, credit: null });

  const toggleRowExpansion = (creditId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(creditId)) {
      newExpanded?.delete(creditId);
    } else {
      newExpanded?.add(creditId);
    }
    setExpandedRows(newExpanded);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(credits?.map(c => c?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectCredit = (creditId, checked) => {
    if (checked) {
      onSelectionChange([...selectedCredits, creditId]);
    } else {
      onSelectionChange(selectedCredits?.filter(id => id !== creditId));
    }
  };

  const getIntegrityBadgeColor = (status) => {
    switch (status) {
      case 'High': return 'bg-success text-success-foreground';
      case 'Medium': return 'bg-warning text-warning-foreground';
      case 'Low': return 'bg-error text-error-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getCCPStatusColor = (status) => {
    switch (status) {
      case 'Approved': return 'text-success';
      case 'Under Review': return 'text-warning';
      case 'Not Approved': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <>
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="w-12 p-4">
                  <input
                    type="checkbox"
                    checked={selectedCredits?.length === credits?.length && credits?.length > 0}
                    onChange={(e) => handleSelectAll(e?.target?.checked)}
                    className="rounded border-border"
                  />
                </th>
                <th className="text-left p-4 font-medium text-card-foreground">Serial Number</th>
                <th className="text-left p-4 font-medium text-card-foreground">Registry</th>
                <th className="text-left p-4 font-medium text-card-foreground">Project</th>
                <th className="text-left p-4 font-medium text-card-foreground">Methodology</th>
                <th className="text-left p-4 font-medium text-card-foreground">Vintage</th>
                <th className="text-left p-4 font-medium text-card-foreground">Quantity</th>
                <th className="text-left p-4 font-medium text-card-foreground">CCP Status</th>
                <th className="text-left p-4 font-medium text-card-foreground">Integrity</th>
                <th className="text-left p-4 font-medium text-card-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {credits?.map((credit) => (
                <React.Fragment key={credit?.id}>
                  <tr className="border-b border-border hover:bg-muted/30 transition-smooth">
                    <td className="p-4">
                      <input
                        type="checkbox"
                        checked={selectedCredits?.includes(credit?.id)}
                        onChange={(e) => handleSelectCredit(credit?.id, e?.target?.checked)}
                        className="rounded border-border"
                      />
                    </td>
                    <td className="p-4">
                      <div className="font-mono text-sm text-card-foreground">{credit?.serialNumber}</div>
                      <div className="text-xs text-muted-foreground">{credit?.registryId}</div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${credit?.registry === 'Verra' ? 'bg-blue-500' : 
                          credit?.registry === 'Gold Standard' ? 'bg-yellow-500' : 
                          credit?.registry === 'ACR' ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                        <span className="text-sm font-medium text-card-foreground">{credit?.registry}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="text-sm font-medium text-card-foreground truncate max-w-48">{credit?.projectName}</div>
                      <div className="text-xs text-muted-foreground">{credit?.projectLocation}</div>
                    </td>
                    <td className="p-4">
                      <div className="text-sm text-card-foreground">{credit?.methodology}</div>
                      <div className="text-xs text-muted-foreground">{credit?.methodologyVersion}</div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm font-medium text-card-foreground">{credit?.vintage}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-sm font-medium text-card-foreground">{credit?.quantity?.toLocaleString()}</span>
                      <div className="text-xs text-muted-foreground">tCO2e</div>
                    </td>
                    <td className="p-4">
                      <span className={`text-sm font-medium ${getCCPStatusColor(credit?.ccpStatus)}`}>
                        {credit?.ccpStatus}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getIntegrityBadgeColor(credit?.integrityLabel)}`}>
                        {credit?.integrityLabel}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="ChevronDown"
                          onClick={() => toggleRowExpansion(credit?.id)}
                          className={`transition-transform ${expandedRows?.has(credit?.id) ? 'rotate-180' : ''}`}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Eye"
                          onClick={() => setDetailsModal({ isOpen: true, credit })}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="GitBranch"
                          onClick={() => setProvenanceModal({ isOpen: true, credit })}
                        />
                      </div>
                    </td>
                  </tr>
                  
                  {/* Expanded Row Details */}
                  {expandedRows?.has(credit?.id) && (
                    <tr className="bg-muted/20">
                      <td colSpan="10" className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div>
                            <h4 className="font-medium text-card-foreground mb-3">MRV Data</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Last Verification:</span>
                                <span className="text-card-foreground">{credit?.lastVerification}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Monitoring Period:</span>
                                <span className="text-card-foreground">{credit?.monitoringPeriod}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Verification Body:</span>
                                <span className="text-card-foreground">{credit?.verificationBody}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-medium text-card-foreground mb-3">Third-Party Ratings</h4>
                            <div className="space-y-2">
                              {credit?.thirdPartyRatings?.map((rating, index) => (
                                <div key={index} className="flex items-center justify-between">
                                  <span className="text-sm text-muted-foreground">{rating?.provider}</span>
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm font-medium text-card-foreground">{rating?.score}</span>
                                    <div className="flex">
                                      {[...Array(5)]?.map((_, i) => (
                                        <Icon
                                          key={i}
                                          name="Star"
                                          size={12}
                                          className={i < rating?.stars ? 'text-warning fill-current' : 'text-muted-foreground'}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-medium text-card-foreground mb-3">Transfer History</h4>
                            <div className="space-y-2">
                              {credit?.transferHistory?.slice(0, 3)?.map((transfer, index) => (
                                <div key={index} className="text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">{transfer?.date}</span>
                                    <span className="text-card-foreground">{transfer?.type}</span>
                                  </div>
                                  <div className="text-xs text-muted-foreground">{transfer?.from} → {transfer?.to}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card Layout */}
        <div className="lg:hidden space-y-4 p-4">
          {credits?.map((credit) => (
            <div key={credit?.id} className="border border-border rounded-lg p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedCredits?.includes(credit?.id)}
                    onChange={(e) => handleSelectCredit(credit?.id, e?.target?.checked)}
                    className="rounded border-border"
                  />
                  <div>
                    <div className="font-mono text-sm font-medium text-card-foreground">{credit?.serialNumber}</div>
                    <div className="text-xs text-muted-foreground">{credit?.registryId}</div>
                  </div>
                </div>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getIntegrityBadgeColor(credit?.integrityLabel)}`}>
                  {credit?.integrityLabel}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Registry:</span>
                  <div className="font-medium text-card-foreground">{credit?.registry}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Vintage:</span>
                  <div className="font-medium text-card-foreground">{credit?.vintage}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Quantity:</span>
                  <div className="font-medium text-card-foreground">{credit?.quantity?.toLocaleString()} tCO2e</div>
                </div>
                <div>
                  <span className="text-muted-foreground">CCP Status:</span>
                  <div className={`font-medium ${getCCPStatusColor(credit?.ccpStatus)}`}>{credit?.ccpStatus}</div>
                </div>
              </div>
              
              <div>
                <div className="text-sm text-muted-foreground">Project:</div>
                <div className="font-medium text-card-foreground">{credit?.projectName}</div>
                <div className="text-xs text-muted-foreground">{credit?.projectLocation}</div>
              </div>
              
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName={expandedRows?.has(credit?.id) ? "ChevronUp" : "ChevronDown"}
                  onClick={() => toggleRowExpansion(credit?.id)}
                >
                  Details
                </Button>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Eye"
                    onClick={() => setDetailsModal({ isOpen: true, credit })}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="GitBranch"
                    onClick={() => setProvenanceModal({ isOpen: true, credit })}
                  />
                </div>
              </div>
              
              {/* Mobile Expanded Details */}
              {expandedRows?.has(credit?.id) && (
                <div className="pt-3 border-t border-border space-y-3">
                  <div>
                    <h5 className="font-medium text-card-foreground mb-2">Methodology</h5>
                    <div className="text-sm text-card-foreground">{credit?.methodology}</div>
                    <div className="text-xs text-muted-foreground">{credit?.methodologyVersion}</div>
                  </div>
                  
                  <div>
                    <h5 className="font-medium text-card-foreground mb-2">Verification</h5>
                    <div className="text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Verified:</span>
                        <span className="text-card-foreground">{credit?.lastVerification}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Body:</span>
                        <span className="text-card-foreground">{credit?.verificationBody}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Modals */}
      <CreditDetailsModal
        isOpen={detailsModal?.isOpen}
        onClose={() => setDetailsModal({ isOpen: false, credit: null })}
        credit={detailsModal?.credit}
      />
      <ProvenanceModal
        isOpen={provenanceModal?.isOpen}
        onClose={() => setProvenanceModal({ isOpen: false, credit: null })}
        credit={provenanceModal?.credit}
      />
    </>
  );
};

export default CreditTable;